import { useState, useEffect, useCallback } from 'react'
import {
  fetchMobileAppSettings,
  saveMobileAppSettings,
  resetMobileAppSettings,
} from '../api/mobileAppApi'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'

export default function useMobileApp() {
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [settings, setSettings] = useState({
    messages: true,
    attendance: true,
    biometric: true,
    lectures: true,
    syllabus: true,
    tests: true,
    marks: true,
    fees: true,
    remarks: true,
    performance: true,
    comparative: true,
    subject: true,
    logoUrl: '',
    appValidTill: '2027-12-31',
    notificationBalance: 24500,
    isExpired: false,
  })

  const loadSettings = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchMobileAppSettings()
      setSettings(data)
    } catch (e) {
      console.error('Failed to load mobile app settings', e)
      appToast.error('Failed to load mobile app settings.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  useEffect(() => {
    const handler = () => loadSettings()
    window.addEventListener('cloud-mobile-app-updated', handler)
    return () => window.removeEventListener('cloud-mobile-app-updated', handler)
  }, [loadSettings])

  const handleToggle = (key, val) => {
    setSettings((prev) => ({ ...prev, [key]: val }))
  }

  const handleLogoChange = (url) => {
    setSettings((prev) => ({ ...prev, logoUrl: url }))
    if (url) {
      appToast.info('Logo updated. Click Save Settings to persist.')
    } else {
      appToast.info('Logo removed.')
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await saveMobileAppSettings(settings)
      appToast.success('Mobile app settings saved successfully.')
    } catch (e) {
      console.error('Failed to save mobile app settings', e)
      appToast.error('Error saving mobile app settings.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = async () => {
    const ok = await appConfirm({
      title: 'Reset Mobile App Settings',
      message: 'Reset all mobile app features to system defaults?',
      confirmText: 'Reset',
      isDestructive: true,
    })
    if (ok) {
      setIsSaving(true)
      try {
        const resetData = await resetMobileAppSettings()
        setSettings(resetData)
        appToast.success('Mobile app settings reset to defaults.')
      } catch (e) {
        console.error('Failed to reset mobile app settings', e)
        appToast.error('Failed to reset settings.')
      } finally {
        setIsSaving(false)
      }
    }
  }

  // Feature Categorization Groups
  const communicationFeatures = [
    {
      key: 'messages',
      label: 'Messages & Notices',
      icon: 'chat',
      description: 'Broadcast messages & circulars',
    },
    {
      key: 'attendance',
      label: 'Daily Attendance',
      icon: 'how_to_reg',
      description: 'Live present / absent status',
    },
    {
      key: 'biometric',
      label: 'Biometric Records',
      icon: 'fingerprint',
      description: 'RFID machine in/out logs',
    },
    {
      key: 'remarks',
      label: 'Faculty Remarks',
      icon: 'rate_review',
      description: 'Behavioral & academic observations',
    },
  ]

  const academicFeatures = [
    {
      key: 'lectures',
      label: 'Lectures Schedule',
      icon: 'cast_for_education',
      description: 'Daily sessions & classroom timetable',
    },
    {
      key: 'syllabus',
      label: 'Syllabus Tracker',
      icon: 'auto_stories',
      description: 'Curriculum & chapter completion',
    },
    {
      key: 'tests',
      label: 'Test Schedule',
      icon: 'quiz',
      description: 'Upcoming tests & exams timetable',
    },
    {
      key: 'marks',
      label: 'Marks & Results',
      icon: 'military_tech',
      description: 'Exam scorecards & test results',
    },
    {
      key: 'fees',
      label: 'Fees & Installments',
      icon: 'payments',
      description: 'Fee ledger & payment dues',
    },
  ]

  const analyticsFeatures = [
    {
      key: 'performance',
      label: 'Performance Graph',
      icon: 'trending_up',
      description: 'Student academic trend line',
    },
    {
      key: 'comparative',
      label: 'Comparative Graph',
      icon: 'stacked_bar_chart',
      description: 'Batch peer percentile comparison',
    },
    {
      key: 'subject',
      label: 'Subject-wise Graph',
      icon: 'donut_small',
      description: 'Per-subject mastery analytics',
    },
  ]

  return {
    state: {
      settings,
      loading,
      isSaving,
      communicationFeatures,
      academicFeatures,
      analyticsFeatures,
    },
    actions: {
      handleToggle,
      handleLogoChange,
      handleSave,
      handleReset,
    },
  }
}
